<template>
  <span
    class="live-badge"
    :class="[`live-badge--${variantModifier}`, `live-badge--${size}`, { 'live-badge--pulse': pulse }]"
  >
    <span class="live-badge__dot" aria-hidden="true" />
    <!-- The dot is decorative in both shapes. With a label the word carries the
         meaning; without one the word is still said, out of a visually hidden
         span, because "running now" signalled in red alone survives neither
         grayscale nor a screen reader. -->
    <span v-if="label" class="live-badge__label">{{ label }}</span>
    <span v-else-if="srLabel" class="live-badge__reader-label">{{ srLabel }}</span>
  </span>
</template>

<script setup>
import { computed } from 'vue';

// The one live-now badge: a red dot, and the word beside it when there is room
// for one. Every surface that says "this is running right now" draws this —
// a sale row, an auction house card, a lane card, a plate over a photo, and the
// bare pip on a dashboard row where the word would not fit.
//
// It was nine hand-drawn copies of two spans, each with its own plate colour and
// its own answer to whether a screen reader hears anything. The dot drifted from
// red to mint to a tinted pill across them, and three of the nine said nothing
// at all without colour.
//
// Presentation only. Whether something is live is the caller's to know; the
// badge only draws it. The label arrives already written in the reader's
// language, so the badge holds no copy of its own.
//
// No slots.
defineOptions({ name: 'LiveBadge' });

const props = defineProps({
  // The word beside the dot, pre-translated — "LIVE", "LIVE NOW",
  // "Live · Lane 1". Absent means the dot alone, and then `srLabel` carries the
  // word for anyone who cannot see the colour.
  label: { type: String, default: null },
  // The word a screen reader hears when there is no visible `label`. REQUIRED
  // whenever `label` is absent: a dot on its own is decorative, so without this
  // the badge announces nothing. Ignored when `label` is set.
  srLabel: { type: String, default: null },
  // How the badge is dressed:
  //   'pill'     — the brand's dark live plate with the word on it. The default,
  //                and what a row, a card or a chip uses.
  //   'plain'    — no plate at all: the dot and the word in the live hue,
  //                inheriting the type around it. For a section eyebrow, or
  //                a dot-only pip in a row of figures.
  //   'onMedia'  — a translucent scrim with a hairline edge, for a badge sitting
  //                over a photo or a video where neither a light nor a dark
  //                plate can be assumed behind it.
  variant: {
    type: String,
    default: 'pill',
    validator: (value) => ['pill', 'plain', 'onMedia'].includes(value),
  },
  // 'md' is the badge beside a title; 'sm' is the one over a thumbnail, where
  // the plate has about a third of the width.
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md'].includes(value),
  },
  // Breathe the dot, and glow it. For the one lane the dealer is watching run,
  // not for every row in a list of them. The theme's reduced-motion reset stops
  // the animation; the glow stays, because a glow is not motion.
  pulse: { type: Boolean, default: false },
});

// `onMedia` is one word in the prop and two in the class, because a BEM modifier
// is kebab-cased and the token derived from it has to be.
const variantModifier = computed(() => (props.variant === 'onMedia' ? 'on-media' : props.variant));
</script>

<style scoped>
.live-badge {
  display: inline-flex;
  align-items: center;
  flex: none;
  gap: var(--p-live-badge-gap);
  white-space: nowrap;
}

/* The two plated shapes share the brand's measurements and differ only in what
   they are painted over. */
.live-badge--pill,
.live-badge--on-media {
  padding: var(--p-live-badge-padding);
  border-radius: var(--p-live-badge-border-radius);
  font-size: var(--p-live-badge-font-size);
  font-weight: var(--p-live-badge-font-weight);
  letter-spacing: var(--p-live-badge-letter-spacing);
}

.live-badge--pill {
  background: var(--p-live-badge-background);
  color: var(--p-live-badge-color);
}

/* Over a photo the badge cannot assume what is behind it, so it brings its own
   ground: a scrim, blurred, with a hairline edge to hold it off a light image. */
.live-badge--on-media {
  background: var(--p-live-badge-on-media-background);
  border: var(--p-live-badge-on-media-border-width) solid var(--p-live-badge-on-media-border-color);
  color: var(--p-live-badge-on-media-color);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* No plate: the dot and the word in the live hue, taking the type of whatever
   line they sit on. A section eyebrow sets its own size and tracking, and a
   badge that restated them would fight it. */
.live-badge--plain {
  color: var(--p-live-badge-plain-color);
}

.live-badge--sm {
  gap: var(--p-live-badge-sm-gap);
}

.live-badge--sm.live-badge--pill,
.live-badge--sm.live-badge--on-media {
  padding: var(--p-live-badge-sm-padding);
  font-size: var(--p-live-badge-sm-font-size);
}

.live-badge__dot {
  width: var(--p-live-badge-dot-size);
  height: var(--p-live-badge-dot-size);
  flex: none;
  border-radius: var(--p-live-badge-border-radius);
  background: var(--p-live-badge-dot);
}

.live-badge--sm .live-badge__dot {
  width: var(--p-live-badge-sm-dot-size);
  height: var(--p-live-badge-sm-dot-size);
}

.live-badge--pulse .live-badge__dot {
  box-shadow: var(--p-live-badge-pulse-dot-box-shadow);
  animation: live-badge-breathe 1.6s ease-in-out infinite;
}

/* The word already arrives capitalized in most languages that have a case; the
   plate is a label rather than a sentence, so it is set that way everywhere. */
.live-badge__label {
  text-transform: uppercase;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.live-badge__reader-label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* A keyframe stop is the animation's shape rather than a paint target, so it is
   exempt from the token tier — see component-token-contract.md. */
@keyframes live-badge-breathe {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.45;
  }
}
</style>
